/**
 * Convert numbers (0 .. 1_000_000_000) to Khmer words and Khmer digits.
 *
 * - numberToKhmerWords(n) -> spelled-out Khmer (script)
 * - numberToKhmerDigits(n) -> Khmer digits (០-៩)
 *
 * Notes:
 * - 1,000,000 = "មួយលាន"
 * - 1,000,000,000 = "មួយពាន់លាន" (one thousand million / one billion)
 *
 * Sources for khmer words/units: MSU Basic Khmer, Wikipedia Khmer numerals.
 * (Used to choose terms like ពាន់, ម៉ឺន, សែន, លាន, ពាន់លាន, ដប់, ម្ភៃ, រយ.)
 */

const ONES: Record<number, string> = {
    0: "សូន្យ",
    1: "មួយ",
    2: "ពីរ",
    3: "បី",
    4: "បួន",
    5: "ប្រាំ",
    6: "ប្រាំមួយ", // 5 + 1 pattern
    7: "ប្រាំពីរ",
    8: "ប្រាំបី",
    9: "ប្រាំបួន"
};

const TENS_SPECIAL: Record<number, string> = {
    10: "ដប់",
    20: "ម្ភៃ"
};

const KHMER_DIGITS2 = /[\u17E0-\u17E9]/g;

const KHMER_MAP: Record<string, string> = {
    "០": "0",
    "១": "1",
    "២": "2",
    "៣": "3",
    "៤": "4",
    "៥": "5",
    "៦": "6",
    "៧": "7",
    "៨": "8",
    "៩": "9"
};

/**
 * Converts any Khmer numerals found in a string to English numerals.
 * Mixed strings like "ឆ្នាំ 2024 ដល់ ២០២៦" become "ឆ្នាំ 2024 ដល់ 2026".
 */
export function normalizeNumerals(input: string, max: number): number {
    const normalized = input
        // Convert Khmer digits to English
        .replace(/[០-៩]/g, (d) => KHMER_MAP[d])
        // Remove anything that is not 0-9
        .replace(/[^0-9]/g, "");

    return normalized ? Math.min(max, parseInt(normalized)) : 0;
}

/**
 * A Type Guard to check if a string contains any Khmer numerals.
 */
function hasKhmerNumerals(input: string): boolean {
    return /[\u17E0-\u17E9]/.test(input);
}

// --- Practical Usage ---

// const mixedData = "Order #102: ចំនួន ១៥ គ្រឿង";

// if (hasKhmerNumerals(mixedData)) {
//     const cleanData = normalizeNumerals(mixedData);
//     console.log(cleanData); // "Order #102: ចំនួន 15 គ្រឿង"
// }

function threeDigitToKhmerWords(n: number): string {
    // Convert 0..999 to Khmer words
    if (n === 0) return "";
    const parts: string[] = [];

    const hundreds = Math.floor(n / 100);
    const rest = n % 100;

    if (hundreds > 0) {
        // e.g., 1 -> "មួយរយ", 2 -> "ពីររយ"
        const hWord = ONES[hundreds] ?? "";
        parts.push(hWord + "រយ");
    }

    if (rest > 0) {
        if (rest < 10) {
            parts.push(ONES[rest]);
        } else if (rest === 10) {
            parts.push(TENS_SPECIAL[10]);
        } else if (rest < 20) {
            // 11..19 = "ដប់" + ones (ដប់មួយ, ដប់ពីរ, ...)
            const ones = rest - 10;
            parts.push(TENS_SPECIAL[10] + (ones ? ONES[ones] : ""));
        } else {
            // 20..99
            if (rest >= 20 && rest < 30) {
                // 20..29: use ម្ភៃ optionally + ones
                const ones = rest - 20;
                const tensWord = TENS_SPECIAL[20]; // ម្ភៃ
                parts.push(tensWord + (ones ? ONES[ones] : ""));
            } else {
                // For 30,40,... we construct as tensDigit + "ម្ភៃ" is not used beyond 20 in common speech.
                // We'll approximate using digit words + "ម៉ឺន"/..." but correct generic approach:
                // fallback: treat as (tensDigit*10) by composing tensDigit + "ដប់"
                // e.g. 30 -> "បីដប់", 40 -> "បួនដប់"
                const tensDigit = Math.floor(rest / 10);
                const ones = rest % 10;
                const tensWord = (ONES[tensDigit] ?? "") + "ដប់"; // e.g., "បីដប់"
                parts.push(tensWord + (ones ? ONES[ones] : ""));
            }
        }
    }

    return parts.join("");
}

const TYPE: "word" | "digit" | "english" = "digit";

export function getRandomNumber(
    min: number,
    max: number,
    language: string
): string {
    const number = Math.floor(Math.random() * (max - min + 1)) + min;
    return numberToKhmerWords(number, language);
}

export function getRandomNumber2(min: number, max: number): number {
    return Math.floor(Math.random() * (max - min + 1)) + min;
}

/**
 * Convert an integer 0..1_000_000_000 into Khmer words. // Dad says not accurate
 */
export function numberToKhmerWords(n: number, language: string): string {
    if (language === "english" || TYPE === "english") return n.toString();
    else if (TYPE === "digit") return numberToKhmerDigits(n);

    if (
        !Number.isFinite(n) ||
        n < 0 ||
        n > 1_000_000_000 ||
        Math.floor(n) !== n
    ) {
        throw new RangeError(
            "number must be an integer between 0 and 1,000,000,000"
        );
    }

    if (n === 0) return ONES[0];

    // Break into parts using Khmer scale: លាន (1,000,000), ពាន់ (1,000), remainder
    // We'll also support grouping for thousands of millions (១,០០០,០០០,០០០) as "ពាន់លាន" for 1,000,000,000.
    const parts: string[] = [];

    // Billions (1,000,000,000) -> "មួយពាន់លាន"
    if (n >= 1_000_000_000) {
        const billions = Math.floor(n / 1_000_000_000); // should be 1 at most here
        parts.push((ONES[billions] ?? `${billions}`) + "ពាន់លាន");
        n = n % 1_000_000_000;
    }

    // Millions (លាន)
    if (n >= 1_000_000) {
        const millions = Math.floor(n / 1_000_000);
        // e.g., 1 -> "មួយលាន", 2 -> "ពីរលាន", etc.
        parts.push(
            (millions === 1
                ? "មួយ"
                : (ONES[millions] ?? threeDigitToKhmerWords(millions))) + "លាន"
        );
        n = n % 1_000_000;
    }

    // Hundreds of thousands / ten-thousands use "សែន" (100k) and "ម៉ឺន" (10k) if needed.
    if (n >= 100_000) {
        const hundredsOfThousands = Math.floor(n / 100_000);
        parts.push(
            (hundredsOfThousands === 1
                ? "មួយ"
                : (ONES[hundredsOfThousands] ??
                  threeDigitToKhmerWords(hundredsOfThousands))) + "សែន"
        );
        n = n % 100_000;
    }

    if (n >= 10_000) {
        const tenThousands = Math.floor(n / 10_000);
        parts.push(
            (tenThousands === 1
                ? "មួយ"
                : (ONES[tenThousands] ??
                  threeDigitToKhmerWords(tenThousands))) + "ម៉ឺន"
        );
        n = n % 10_000;
    }

    // Thousands
    if (n >= 1000) {
        const thousands = Math.floor(n / 1000);
        parts.push(
            (thousands === 1
                ? "មួយ"
                : (ONES[thousands] ?? threeDigitToKhmerWords(thousands))) +
                "ពាន់"
        );
        n = n % 1000;
    }

    // Remainder < 1000
    if (n > 0) {
        parts.push(threeDigitToKhmerWords(n));
    }

    // Join and return. Remove empty segments.
    return parts.filter(Boolean).join("");
}

/**
 * Convert to Khmer digits (០ ១ ២ ... ៩)
 */
const KHMER_DIGITS = ["០", "១", "២", "៣", "៤", "៥", "៦", "៧", "៨", "៩"];
export function numberToKhmerDigits(n: number): string {
    if (
        !Number.isFinite(n) ||
        n < 0 ||
        n > 1_000_000_000 ||
        Math.floor(n) !== n
    ) {
        throw new RangeError(
            "number must be an integer between 0 and 1,000,000,000"
        );
    }
    return String(n)
        .split("")
        .map((ch) => {
            if (ch === "-") return ch;
            if (ch === ",") return ch;
            const d = parseInt(ch, 10);
            return isNaN(d) ? ch : KHMER_DIGITS[d];
        })
        .join("");
}

/* Quick examples:
console.log(numberToKhmerDigits(12345)); // "១២៣៤៥"
console.log(numberToKhmerWords(0)); // "សូន្យ"
console.log(numberToKhmerWords(5)); // "ប្រាំ"
console.log(numberToKhmerWords(16)); // "ដប់ប្រាំមួយ" (constructed as ដប់ + ប្រាំមួយ)
console.log(numberToKhmerWords(1234567)); // "មួយលានពីរម៉ឺន..." (approx)
*/
